import { useState } from 'react';
import { Button, Choice, Icon, Input, Modal, Reason } from '@efira/ui';

/**
 * Подборщик изображения: четыре вкладки, как у эталона.
 *
 * Библиотека, загрузка файла, ссылка, поиск гифок
 * (docs/reference/LIVECADE_UX.md, 4.2). У эталона это панель прямо в
 * колонке; у нас окно: колонка шириной 300 px не вмещает сетку изображений,
 * а сжимать её до двух превью значит показывать библиотеку, по которой
 * ничего нельзя выбрать.
 *
 * ЧЕГО ЗДЕСЬ НАМЕРЕННО НЕТ. Хранилища своих изображений у нас пока нет:
 * библиотека пуста и говорит об этом прямо, а не рисует заглушки. Загрузка
 * файла и поиск гифок объявлены недоступными С ПРИЧИНОЙ и с тем, что будет
 * взамен, - правило «кнопка, которая ничего не делает, хуже отсутствующей»
 * не позволяет нарисовать их работающими. Ссылка работает полностью: её
 * достаточно, чтобы поставить фон сегодня.
 */
type Tab = 'library' | 'upload' | 'url' | 'gif';

const TABS: readonly (readonly [Tab, string])[] = [
  ['library', 'Библиотека'],
  ['upload', 'Загрузить'],
  ['url', 'Ссылка'],
  ['gif', 'Гифки'],
];

export function MediaPicker({
  gameId,
  current,
  onClose,
  onPick,
}: {
  gameId: string;
  current: string;
  onClose: () => void;
  onPick: (url: string) => void;
}) {
  /* Открывается на библиотеке, как у эталона: её пустое состояние само
     говорит, что делать дальше. */
  const [tab, setTab] = useState<Tab>('library');
  const [url, setUrl] = useState(current);

  return (
    <Modal
      open
      onClose={onClose}
      title="Фоновое изображение"
      /* Набранная ссылка - несохранённая правка: промах мимо окна не должен
         стирать её молча. */
      dirty={url.trim() !== current}
      actions={
        <Button variant="secondary" onClick={onClose}>
          Закрыть
        </Button>
      }
    >
      <div data-media-picker={gameId} className="flex flex-col gap-3">
        <Choice
          label="Разделы медиа игры"
          value={tab}
          onChange={setTab}
          size="sm"
          data-control-row="media-tabs"
          options={TABS.map(([id, title]) => ({
            value: id,
            label: title,
            data: { 'data-media-tab': id },
          }))}
        />

        {tab === 'library' && (
          <p data-media-panel="library" className="text-foreground/80 text-sm">
            Своей библиотеки изображений пока нет. Поставьте фон ссылкой — вкладка «Ссылка».
          </p>
        )}

        {tab === 'upload' && (
          <div data-media-panel="upload" className="flex flex-col gap-2">
            <Button variant="secondary" size="s" className="self-start" disabled>
              Выбрать файл
            </Button>
            {/* Недоступность объяснена и названо, что делать вместо. */}
            <p className="text-foreground/80 text-sm">
              Загрузка своих файлов появится вместе с хранилищем материалов. Пока поставьте фон
              ссылкой на PNG, JPEG, WebP или GIF.
            </p>
          </div>
        )}

        {tab === 'url' && (
          <div data-media-panel="url" className="flex flex-col gap-2">
            <label className="flex flex-col gap-1 text-sm">
              Ссылка на изображение
              <Input
                data-media-url
                type="url"
                inputMode="url"
                placeholder="https://..."
                value={url}
                onChange={(event) => {
                  setUrl(event.target.value);
                }}
              />
            </label>
            <p className="text-foreground/80 text-sm">
              Только https: по http картинка на странице оверлея не загрузится и оставит пустой фон.
              PNG, JPEG, WebP, GIF.
            </p>

            <div className="flex gap-2">
              <Button
                variant="primary"
                size="s"
                data-media-apply
                /* Кнопка при пустом поле видна и неактивна, а не спрятана;
                   причина - словами под ней (правило `UI-10`). */
                disabled={!/^https:\/\/\S+$/.test(url.trim())}
                aria-describedby={/^https:\/\/\S+$/.test(url.trim()) ? undefined : 'media-url-why'}
                onClick={() => {
                  onPick(url.trim());
                }}
              >
                Применить
              </Button>

              {current !== '' && (
                <Button
                  variant="danger"
                  size="s"
                  icon={<Icon name="delete" size="sm" />}
                  data-media-clear
                  data-removal="media-clear"
                  onClick={() => {
                    onPick('');
                  }}
                >
                  Убрать изображение
                </Button>
              )}
            </div>
            {!/^https:\/\/\S+$/.test(url.trim()) && (
              <Reason id="media-url-why">Впишите ссылку, начиная с https://</Reason>
            )}
          </div>
        )}

        {tab === 'gif' && (
          <div data-media-panel="gif" className="flex flex-col gap-2">
            <Input placeholder="Поиск гифок" disabled />
            <p className="text-foreground/80 text-sm">
              Поиск гифок появится вместе с падами: справочник у них общий. Пока поставьте гифку
              ссылкой — вкладка «Ссылка».
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}
